import API_CONFIG from '../../apiConfig';

export interface GenerateDesignPayload {
  clothImage: string;
  clothImage2: string | null;
  gender: string;
  clothType: string;
  clothAttributes: Record<string, string[]>;
  designTheme: string;
  colorPalette: string;
  style: string;
  aspectRatio: string;
  prompt: string;
  numImages: number;
  mode: string;
  userId: string;
  outputFormat: 'jpeg';
  enableSafetyChecker: boolean;
  type: 'ai-designer';
}

export interface GenerateDesignResult {
  success: boolean;
  imageUrls?: string[];
  message?: string;
}

export const uploadClothImage = async (
  file: File,
  userId: string,
  signal?: AbortSignal
): Promise<string> => {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('userId', userId || 'anonymous');

  const response = await fetch(API_CONFIG.AI_DESIGNER_UPLOAD_URL, {
    method: 'POST',
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error('Upload failed. Please try again.');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Upload failed. Please try again.');
  }

  const url: string | undefined =
    data.uploaded?.[0]?.url || data.urls?.[0] || data.url;

  if (!url) {
    throw new Error('Upload failed. No image was returned.');
  }

  return url;
};

export const generateDesign = async (
  payload: GenerateDesignPayload,
  signal?: AbortSignal
): Promise<GenerateDesignResult> => {
  const response = await fetch(API_CONFIG.AI_DESIGNER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    throw new Error('Something went wrong. Please try again.');
  }

  return response.json();
};

export const downloadImage = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
